import { MouseEventHandler } from 'react';
import { useNavigate } from 'react-router-dom';

import { FeatureFlagged } from '@backstage/core-app-api';

import { Banner, Button, Flex, Text } from '@spotify-internal/encore-web';

import { ConfidenceLogo } from '../Icons';
import { ChartBackground } from './ChartBackground';
import { Footer } from './Footer';
import { LoginErrorMessage } from './LoginErrorMessage';

import styles from './LoginPage.module.css';

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- Pre-auth page in core-react cannot depend on tracking-react
export const LoginPage = ({
  onLogin,
  loading,
  error,
}: {
  onLogin?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  error?: Error;
}) => {
  const navigate = useNavigate();

  return (
    <ChartBackground>
      <Flex
        className={styles.root}
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        data-testid="login-page-root"
      >
        <Flex direction="column" alignItems="center">
          <ConfidenceLogo className={styles.logo} color="currentColor" />
          <Text as="h1" variant="titleLarge" className={styles.title}>
            Have an idea.
            <br /> Test it. Ship it. <br /> With Confidence.
          </Text>
          <div className={styles.container}>
            {error && (
              <Banner colorSet="negative" contextual role="alert">
                <LoginErrorMessage error={error} />
              </Banner>
            )}
            <Flex className={styles.actions} gap="base" justifyContent="center">
              <Button
                variant="primary"
                size="large"
                fullWidth
                data-testid="login-button"
                onClick={onLogin}
                disabled={loading}
                isLoading={loading}
                loadingAriaLabel="Authenticating"
              >
                Log in
              </Button>
              <FeatureFlagged with="self-serve">
                <Button
                  variant="secondary-bordered"
                  size="large"
                  fullWidth
                  data-testid="signup-button"
                  onClick={() => navigate('/signup')}
                  disabled={loading}
                >
                  Sign up
                </Button>
              </FeatureFlagged>
            </Flex>
          </div>
        </Flex>
        <Footer />
      </Flex>
    </ChartBackground>
  );
};
